import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { type FieldTypeWithOnlyValues } from '@databases/field-value';
import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import { useIsMounted } from '@databases/react';
import { defaultStoreUtils, type DefaultRootState } from '@databases/redux';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import {
  type JiraIssueRef,
  type ResolvedJiraIssueRef
} from '@databases/types/confluence';
import { type DatabaseFieldType } from '@databases/types/database';
import {
  getReferencedJiraIssueRefs,
  getReferencedJiraValues
} from '@databases/utils/database/field-types/jiraIssueDetails';
import { getDatabaseEntryRequired } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { loadJiraIssue } from '@databases/utils/redux/jira/actions';
import { selectJiraIssueOptional } from '@databases/utils/redux/jira/selectors';
import { useCallback, useState } from 'react';

import { smartLinkClient } from '@canvas/smart-link-client';

import { type EditComponentProps } from '../types.ts';

import { updateIssuePriority } from './api.ts';

const selectJiraPriorityData = (
  state: DefaultRootState,
  {
    contentId,
    entryId,
    field
  }: {
    contentId: string;
    entryId: string | undefined;
    field: EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>['field'];
  }
) => {
  const database = selectDatabaseByContentIdRequired(state, { contentId });
  const entry = entryId ? getDatabaseEntryRequired(database, entryId) : undefined;

  const issueRefs = entry
    ? getReferencedJiraIssueRefs({ database, field, entry }, state)
    : [];

  const jiraValues = getReferencedJiraValues({ database, field, entry }, state);
  const priorityItems = jiraValues.reduce((acc, curr) => {
    if (curr && curr.data) {
      const values = Array.isArray(curr.data) ? curr.data : [curr.data];
      acc.push({
        type: curr.type,
        text: curr.text,
        values
      } as FieldTypeWithOnlyValues);
    }
    return acc;
  }, [] as FieldTypeWithOnlyValues[]);

  const resolvedIssues = issueRefs.map((ref) =>
    ref ? selectJiraIssueOptional(state, ref) : undefined
  );

  return {
    issueRefs,
    priorityItems,
    resolvedIssues
  };
};

export interface JiraPriorityData {
  issueRefs: JiraIssueRef[];
  priorityItems: FieldTypeWithOnlyValues[];
  resolvedIssues: (ResolvedJiraIssueRef | undefined)[];
  updatePriority: (
    issueRef: JiraIssueRef,
    priorityId: string,
    currentPriorityId?: string
  ) => Promise<void>;
  isUpdating: (issueRef: JiraIssueRef) => boolean;
}

// Single consolidated selector that handles both data fetching and actions
export function useJiraPriorityData(
  contentId: string,
  entryId: string | undefined,
  field: EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>['field']
): JiraPriorityData {
  const [updatingIssues, setUpdatingIssues] = useState<Set<string>>(new Set());
  const isMountedRef = useIsMounted();
  const dispatch = defaultStoreUtils.useDispatch();
  const analytics = useAnalytics();

  const updatePriority = useCallback(
    async (issueRef: JiraIssueRef, priorityId: string, currentPriorityId?: string) => {
      if (!issueRef?.ari) {
        return;
      }

      // Prevent updating to the same priority
      if (currentPriorityId != null && priorityId === currentPriorityId) {
        return;
      }

      const issueKey = issueRef.key || issueRef.ari || '';
      setUpdatingIssues((prev) => new Set(prev).add(issueKey));

      try {
        await updateIssuePriority({
          priorityId,
          issueAri: issueRef.ari
        });

        // Send analytics event for successful priority update
        analytics.sendJiraFieldUpdatedEvent(
          {
            contentId,
            issueKey: issueRef.key,
            fieldType: 'priority',
            databaseContainer: getDatabaseContainerType()
          },
          'jiraPriority',
          'jiraPriorityValueEdit'
        );

        const resolvedIssue = await dispatch(
          loadJiraIssue(issueRef, { columns: ['priority'], forceReload: true })
        );
        if (resolvedIssue?.url) {
          smartLinkClient.forceRefreshData(resolvedIssue.url);
        }
      } catch (e) {
        logError(
          'jira-priority/update:failed',
          e instanceof Error
            ? e
            : new Error(`jira priority update failed for issue ${issueRef.key}`)
        );
        currentSnackbarState.setMessage(
          messages['jira-priority.update-failed'].defaultMessage,
          {
            id: messages['jira-priority.update-failed'].id,
            type: 'error',
            showCloseButton: true,
            hideAfter: 4000
          }
        );
        return;
      } finally {
        if (isMountedRef.current) {
          setUpdatingIssues((prev) => {
            const newSet = new Set(prev);
            newSet.delete(issueKey);
            return newSet;
          });
        }
      }
    },
    [dispatch, isMountedRef, contentId, analytics]
  );

  const data = defaultStoreUtils.useEqualSelector((state) =>
    selectJiraPriorityData(state, { contentId, entryId, field })
  );

  const isUpdating = useCallback(
    (issueRef: JiraIssueRef) => {
      const issueKey = issueRef.key || issueRef.ari || '';
      return updatingIssues.has(issueKey);
    },
    [updatingIssues]
  );

  return {
    ...data,
    updatePriority,
    isUpdating
  };
}
